import { DropdownMenu, DropdownMenuItem } from '@/components/Elements';
import {
  useFilterConditionAtomValue,
  useSetFilterConditionAtom,
} from '@/features/analyze/atoms/filterCondition.atom';
import { convertSavedFilterConditionToFilterCondition } from '@/features/analyze/lib/convertSavedFilterCondition';
import { FragmentOf, graphql, readFragment, useMutation } from '@/lib/graphql';
import { removeEmptyValues } from '@/utils/removeEmptyValues';
import {
  CheckIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { BellIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplyFilterConfirmModal } from './ApplyFilterConfirmModal';
import { DeleteFilterConfirmModal } from './DeleteFilterConfirmModal';
import { EditSavedFilterModal } from './EditSavedFilterModal';
import { savedFilterItemFragment } from './fragments';

// フィルター削除用のMutation
const deleteSavedFilterMutation = graphql(
  `
    mutation DeleteSavedFilter($input: DeleteSavedFilterInput!) {
      deleteSavedFilter(input: $input) {
        success
        userErrors {
          code
          message
          path
        }
        savedFilter {
          ...SavedFilterItemFragment
        }
      }
    }
  `,
  [savedFilterItemFragment]
);

type SavedFilterItemProps = {
  data: FragmentOf<typeof savedFilterItemFragment>;
  className?: string;
};

export const SavedFilterItem = ({ data, className }: SavedFilterItemProps) => {
  const { t } = useTranslation();
  const savedFilter = readFragment(savedFilterItemFragment, data);
  const currentFilterCondition = useFilterConditionAtomValue();
  const setFilterCondition = useSetFilterConditionAtom();
  const [deleteSavedFilter, { loading: isLoading }] = useMutation(
    deleteSavedFilterMutation,
    {
      update: (cache, { data }) => {
        if (data?.deleteSavedFilter?.success) {
          cache.evict({ id: cache.identify(savedFilter) });
          cache.gc();
        }
      },
    }
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApplyConfirmModal, setShowApplyConfirmModal] = useState(false);

  const handleApplyFilter = () => {
    const filterCondition = convertSavedFilterConditionToFilterCondition(
      savedFilter.condition
    );

    // 現在のフィルター条件が空でない場合、警告を表示
    const nonEmptyCurrentCondition = removeEmptyValues(currentFilterCondition);
    if (Object.keys(nonEmptyCurrentCondition).length > 0) {
      setShowApplyConfirmModal(true);
      return;
    }

    // フィルター条件が空の場合は直接適用
    setFilterCondition(filterCondition);
  };

  const handleConfirmApplyFilter = () => {
    const filterCondition = convertSavedFilterConditionToFilterCondition(
      savedFilter.condition
    );
    setFilterCondition(filterCondition);
    setShowApplyConfirmModal(false);
  };

  const handleDeleteFilter = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    const result = await deleteSavedFilter({
      variables: {
        input: { id: savedFilter.id },
      },
    });

    if (result.data?.deleteSavedFilter?.success) {
      setShowDeleteModal(false);
    }
  };

  const handleEditFilter = () => {
    setShowEditModal(true);
  };

  const menuItems = [
    {
      label: t('反映'),
      icon: CheckIcon,
      onClick: handleApplyFilter,
    },
    {
      label: t('編集'),
      icon: PencilSquareIcon,
      onClick: handleEditFilter,
    },
    {
      label: t('削除'),
      icon: TrashIcon,
      onClick: handleDeleteFilter,
    },
  ];

  return (
    <>
      <div className={clsx('flex items-center justify-between', className)}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="min-w-0 flex-1 truncate">{savedFilter.name}</span>
          {savedFilter.alertEnabled && (
            <BellIcon
              className="mr-2 size-4 shrink-0 text-yellow-500"
              title={t('通知設定済み')}
            />
          )}
        </div>

        <DropdownMenu
          trigger={<EllipsisVerticalIcon className="size-4" />}
          anchor="bottom end"
        >
          {menuItems.map((item) => (
            <DropdownMenuItem key={item.label} onClick={item.onClick}>
              <item.icon className="size-4" />
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
      </div>

      <DeleteFilterConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        filterName={savedFilter.name}
        isLoading={isLoading}
      />

      <EditSavedFilterModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        filterId={savedFilter.id}
        currentName={savedFilter.name}
        currentAlertEnabled={savedFilter.alertEnabled}
      />

      <ApplyFilterConfirmModal
        isOpen={showApplyConfirmModal}
        onClose={() => setShowApplyConfirmModal(false)}
        onConfirm={handleConfirmApplyFilter}
        filterName={savedFilter.name}
      />
    </>
  );
};

SavedFilterItem.fragments = {
  data: savedFilterItemFragment,
};
